<script setup lang="ts">
import CookieIllustration from "@/assets/icons/cookie-illustration.svg";
import CheckIcon from "@/assets/icons/checkmark.svg";
import CloseIcon from "@/assets/icons/close.svg";
import { inject } from "vue";
import { ThemeButton } from "@/ui-components";
import { storeToRefs } from "pinia";
import { useUiStore } from "@/stores/ui.ts";
import { useRouter } from "vue-router";
import { type Breakpoints, useBreakpoints } from "@vueuse/core";

const { isCookieBanner } = storeToRefs(useUiStore());
const router = useRouter();
const breakpoints = useBreakpoints(inject("breakpoints") as Breakpoints<string>);
// @ts-ignore
const _paq = (window._paq = window._paq || []);

const onDeny = () => {
  isCookieBanner.value = false;
  _paq.push(["forgetConsentGiven"]);
  _paq.push(["forgetCookieConsentGiven"]);
  _paq.push(["optUserOut"]);
};

const openDataProtection = () => {
  const routeData = router.resolve({ name: "data-protection" });
  window.open(routeData.href, "_blank");
};

const onAllow = () => {
  isCookieBanner.value = false;
  _paq.push(["rememberConsentGiven"]);
  _paq.push(["rememberCookieConsentGiven"]);
  _paq.push(["forgetUserOptOut"]);
};
</script>

<template>
  <div class="holder d-flex align-items-center justify-content-center">
    <div class="overlay d-flex align-items-center justify-content-center" />
    <div class="banner p-sm-6 p-3">
      <h3
        class="h3 mb-3 font-weight-bold"
        :class="{ smAndDown: breakpoints.smallerOrEqual('sm').value }"
      >
        Bevor es losgehen kann, das obligatorische Rechtliche:
      </h3>
      <CookieIllustration class="banner-illustration mb-3 d-block mx-auto" />
      <p class="mb-6">
        Wir verwenden Cookies zur statistischen Nutzungsanalyse und zur Optimierung der Seite. Es
        erfolgt keine Weitergabe an Dritte.
        <a class="banner-link mx-1" href="/data-protection" @click.prevent="openDataProtection">
          Mehr erfahren
        </a>
      </p>
      <div class="d-flex justify-content-center align-items-center">
        <ThemeButton class="banner-action me-2" variant="light" :tabindex="1" @click="onDeny">
          Ablehnen
          <CloseIcon class="ms-1 icon" />
        </ThemeButton>
        <ThemeButton variant="primary" :tabindex="2" @click="onAllow">
          Akzeptieren
          <CheckIcon class="ms-1 icon icon-check" />
        </ThemeButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.holder {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--black);
  opacity: 0.5;
}

.banner {
  max-width: 512px;
  max-height: 85vh;
  overflow: auto;
  width: 96%;
  border-radius: 4px;
  background-color: var(--white);
  color: var(--black);
  z-index: 9999;
  animation: fadeIn 1s var(--transition-main-function) forwards;

  .h3 {
    &.smAndDown {
      font-size: 23px;
    }
  }

  &-illustration {
    max-width: 311px;
  }

  &-link {
    text-decoration: underline;
  }

  &-action {
    line-height: 1rem;
    font-weight: 700;
    transition: all var(--transition-default-time) var(--transition-main-function);
    cursor: pointer;

    &:hover {
      border-color: var(--white);
    }
  }

  .icon {
    width: 16px;

    &-check {
      width: 20px;
    }
  }
}
</style>
